"use strict";
/**
 * Date utilities for UTC storage and UTC display.
 * - All dates sent to the server are UTC ISO strings.
 * - Date-only values (e.g. from HTML date input) represent UTC midnight.
 * - All display uses UTC so times never shift due to the browser's local timezone.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.localDateStringToUTC = localDateStringToUTC;
exports.utcToLocalDateString = utcToLocalDateString;
exports.formatDateForDisplay = formatDateForDisplay;
exports.isDateOnlyString = isDateOnlyString;
exports.isEventLiveToday = isEventLiveToday;
exports.isEventPast = isEventPast;
exports.registrationCloseExclusive = registrationCloseExclusive;
/** Parse "YYYY-MM-DD" as UTC midnight and return UTC ISO string for server */
function localDateStringToUTC(dateString) {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toISOString();
}
/** Convert UTC ISO/Date to "YYYY-MM-DD" for date input value (UTC date) */
function utcToLocalDateString(utc) {
    const d = new Date(utc);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
/** Format UTC date for display, always in UTC so the date never shifts */
function formatDateForDisplay(utc, options) {
    if (utc == null)
        return '';
    return new Date(utc).toLocaleDateString('en-US', {
        ...(options ?? { year: '2-digit', month: 'numeric', day: 'numeric' }),
        timeZone: 'UTC',
    });
}
/** Check if a value looks like a YYYY-MM-DD date string */
function isDateOnlyString(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}
/** True if today (UTC date) falls within the event's startDate..endDate inclusive */
function isEventLiveToday(startDate, endDate) {
    const today = utcToLocalDateString(new Date());
    const start = utcToLocalDateString(startDate);
    const end = utcToLocalDateString(endDate);
    return today >= start && today <= end;
}
/** True if today (UTC date) is strictly after the event's end date. Use for "Past" so live-on-end-day events are not marked past. */
function isEventPast(endDate) {
    const today = utcToLocalDateString(new Date());
    const end = utcToLocalDateString(endDate);
    return today > end;
}
// ---------------------------------------------------------------------------
// Registration gate — local-calendar boundary helpers
//
// These mirror the server-side promotionEndExclusive logic in
// mcDiscountsCreditsEligibility.ts so that a regCloseDate stored as UTC
// midnight is treated as "end of that calendar day in the user's local
// timezone" rather than the exact UTC instant.
// ---------------------------------------------------------------------------
/** True when the ISO string is a bare date or a UTC-midnight timestamp. */
function isDateOnlyBoundary(iso) {
    const t = iso.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(t) || /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/.test(t);
}
/** Return the YYYY-MM-DD string for the day after `ymd`. */
function nextCalendarYmd(ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + 1));
    return dt.toISOString().slice(0, 10);
}
/** Format a UTC timestamp as YYYY-MM-DD in the given IANA timezone. */
function ymdInZone(t, timeZone) {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(t);
    }
    catch {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'UTC',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(t);
    }
}
/**
 * Binary-search for the first UTC millisecond where the calendar date in
 * `timeZone` equals `ymd` (YYYY-MM-DD).
 */
function firstInstantOfLocalDay(ymd, timeZone) {
    const [y, mo, d] = ymd.split('-').map(Number);
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d))
        return new Date(NaN);
    let lo = Date.UTC(y, mo - 1, d) - 72 * 3600000;
    let hi = Date.UTC(y, mo - 1, d) + 72 * 3600000;
    let guard = 0;
    while (ymdInZone(lo, timeZone) >= ymd && guard++ < 400)
        lo -= 86400000;
    guard = 0;
    while (ymdInZone(hi, timeZone) < ymd && guard++ < 400)
        hi += 86400000;
    while (lo < hi - 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (ymdInZone(mid, timeZone) < ymd)
            lo = mid;
        else
            hi = mid;
    }
    return new Date(hi);
}
/**
 * Returns the exclusive deadline for a registration close date, interpreted
 * in the user's local timezone.
 *
 * - If `closeDate` is a date-only UTC boundary (bare date or UTC midnight),
 *   the deadline is extended to midnight of the *next* calendar day in
 *   `timeZone`, so registration stays open through the end of the displayed
 *   calendar date for users behind UTC.
 * - If `closeDate` carries an explicit time, it is returned as-is (exact
 *   UTC instant, no adjustment).
 *
 * Obtain `timeZone` via `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 */
function registrationCloseExclusive(closeDate, timeZone) {
    if (closeDate == null)
        return new Date(NaN);
    const iso = closeDate instanceof Date ? closeDate.toISOString() : String(closeDate).trim();
    if (isDateOnlyBoundary(iso)) {
        return firstInstantOfLocalDay(nextCalendarYmd(iso.slice(0, 10)), timeZone);
    }
    return new Date(iso);
}
